import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Reveal from "./Reveal";
import Sprig from "./Sprig";
import useReveal from "../hooks/useReveal";
import categories from "../data/categories";
import { useLang } from "../i18n/LanguageContext";

const easeOut = (p) => 1 - Math.pow(1 - p, 3);

function CountUp({ to, suffix = "" }) {
  const [ref, shown] = useReveal({ threshold: 0.4 });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!shown) return;
    let raf;
    const dur = 1300;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / dur);
      setN(Math.round(easeOut(p) * to));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [shown, to]);
  return (
    <b ref={ref}>
      {n}
      {suffix}
    </b>
  );
}

const ICONS = {
  curated: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l2.2 5 5.3.4-4 3.5 1.2 5.2L12 19l-4.7 2.6 1.2-5.2-4-3.5 5.3-.4z" />
    </svg>
  ),
  free: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20s-7-4.4-9.2-8.4C1.3 8.5 3 5.5 6 5.5c1.9 0 3.2 1.1 4 2.3.8-1.2 2.1-2.3 4-2.3 3 0 4.7 3 3.2 6.1C19 15.6 12 20 12 20z" />
    </svg>
  ),
  contact: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.4 8.4 0 0 1-12 7.6L3 21l1.9-6A8.5 8.5 0 1 1 21 11.5z" />
    </svg>
  ),
  local: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="2.6" />
    </svg>
  ),
};

export default function AboutSection() {
  const { lang } = useLang();
  const sq = lang === "sq";

  const pillars = [
    {
      key: "curated",
      t: sq ? "Të përzgjedhura me kujdes" : "Carefully curated",
      d: sq ? "Jo një listë e pafund — vetëm biznese që ia vlejnë." : "Not an endless list — only businesses worth your time.",
    },
    {
      key: "free",
      t: sq ? "Falas për çiftet" : "Free for couples",
      d: sq ? "Kërko, ruaj dhe kontakto pa paguar asnjë cent." : "Browse, save and contact without paying a cent.",
    },
    {
      key: "contact",
      t: sq ? "Kontakt i drejtpërdrejtë" : "Direct contact",
      d: sq ? "Telefono, WhatsApp ose Instagram — pa ndërmjetës." : "Call, WhatsApp or Instagram — no middleman.",
    },
    {
      key: "local",
      t: sq ? "Ndërtuar për ne" : "Built for us",
      d: sq ? "Kosovë, Shqipëri e Maqedoni, në gjuhën tënde." : "Kosovo, Albania and Macedonia, in your language.",
    },
  ];

  const stats = [
    { n: categories.length, suffix: "", label: sq ? "kategori" : "categories" },
    { n: 3, suffix: "", label: sq ? "shtete" : "countries" },
    { n: 100, suffix: "%", label: sq ? "falas për çiftet" : "free for couples" },
  ];

  return (
    <Section id="about">
      <Glow />
      <Float>
        <Sprig width={120} color="#C9B68E" />
      </Float>

      <Inner>
        <Reveal>
          <Eyebrow>✦ {sq ? "Rreth nesh" : "About us"}</Eyebrow>
        </Reveal>
        <Reveal delay={80}>
          <Headline>
            {sq ? "Planifikimi i dasmës," : "Wedding planning,"}
            <br />
            <em>{sq ? "më në fund i lehtë." : "finally made easy."}</em>
          </Headline>
        </Reveal>
        <Reveal delay={150}>
          <Lead>
            {sq
              ? "Amora Weddings lindi nga një ide e thjeshtë: gjetja e duhur nuk duhet të jetë kaos mesazhesh dhe kërkimesh të pafundme. I sjellim bizneset më të mira të dasmave — salla, fotografë, dekor, muzikë e më shumë — në një vend të vetëm."
              : "Amora Weddings started from a simple idea: finding the right people shouldn't be a chaos of DMs and endless searching. We bring the best wedding businesses — venues, photographers, decor, music and more — into one place."}
          </Lead>
        </Reveal>

        <Pillars>
          {pillars.map((p, i) => (
            <Reveal key={p.key} delay={i * 90}>
              <Pillar>
                <span className="mark">{ICONS[p.key]}</span>
                <h3>{p.t}</h3>
                <p>{p.d}</p>
              </Pillar>
            </Reveal>
          ))}
        </Pillars>

        <Band>
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 120}>
              <Stat>
                <CountUp to={s.n} suffix={s.suffix} />
                <span>{s.label}</span>
              </Stat>
            </Reveal>
          ))}
        </Band>

        <Reveal delay={60}>
          <Closing>
            {sq
              ? "Misioni ynë është i thjeshtë: ta bëjmë dasmën më të lehtë për çiftet, dhe të ndihmojmë bizneset lokale të rriten."
              : "Our mission is simple: make weddings easier for couples, and help local businesses grow."}
          </Closing>
        </Reveal>
        <Reveal delay={140}>
          <Ctas>
            <Link className="primary" to="/list">
              {sq ? "Regjistro biznesin" : "List your business"}
            </Link>
            <a href="#explore">{sq ? "Fillo kërkimin →" : "Start searching →"}</a>
          </Ctas>
        </Reveal>
      </Inner>
    </Section>
  );
}

const Section = styled.section`
  position: relative;
  overflow: hidden;
  background: linear-gradient(180deg, ${({ theme }) => theme.colors.cream} 0%, ${({ theme }) => theme.colors.creamDeep} 100%);
  border-top: 1px solid ${({ theme }) => theme.colors.hair};
  padding: clamp(64px, 10vw, 130px) clamp(20px, 5vw, 40px);
`;

const Glow = styled.div`
  position: absolute;
  top: -180px;
  right: -160px;
  width: 480px;
  height: 480px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(217, 140, 130, 0.16), transparent 70%);
  pointer-events: none;
`;

const Float = styled.div`
  position: absolute;
  left: -28px;
  bottom: 40px;
  opacity: 0.4;
  animation: drift 9s ease-in-out infinite;
  pointer-events: none;
  @keyframes drift {
    0%, 100% { transform: translateY(0) rotate(-6deg); }
    50% { transform: translateY(-18px) rotate(4deg); }
  }
  @media (max-width: 720px) { display: none; }
`;

const Inner = styled.div`
  position: relative;
  max-width: 920px;
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled.div`
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.rose};
`;

const Headline = styled.h2`
  margin-top: 16px;
  font-family: ${({ theme }) => theme.font.display};
  font-weight: 500;
  font-size: clamp(34px, 6vw, 62px);
  line-height: 1.04;
  color: ${({ theme }) => theme.colors.ink};
  em { font-style: italic; color: ${({ theme }) => theme.colors.green}; }
`;

const Lead = styled.p`
  margin: 24px auto 0;
  max-width: 620px;
  font-size: 17px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.inkSoft};
`;

const Pillars = styled.div`
  margin-top: clamp(40px, 6vw, 64px);
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  text-align: left;
  @media (max-width: 860px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 460px) { grid-template-columns: 1fr; }
`;

const Pillar = styled.div`
  height: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.hair};
  border-radius: 18px;
  padding: 22px 20px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  &:hover { transform: translateY(-4px); box-shadow: 0 14px 34px rgba(47, 74, 60, 0.1); }
  .mark {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: ${({ theme }) => theme.colors.creamDeep};
    color: ${({ theme }) => theme.colors.green};
    margin-bottom: 14px;
  }
  .mark svg { width: 22px; height: 22px; }
  h3 { font-size: 16px; font-weight: 600; color: ${({ theme }) => theme.colors.ink}; margin-bottom: 6px; }
  p { font-size: 13.5px; line-height: 1.55; color: ${({ theme }) => theme.colors.inkSoft}; }
`;

const Band = styled.div`
  margin: clamp(44px, 6vw, 70px) auto 0;
  max-width: 680px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  padding: clamp(24px, 4vw, 36px);
  border-radius: 22px;
  background: ${({ theme }) => theme.colors.greenDeep};
  color: ${({ theme }) => theme.colors.cream};
`;

const Stat = styled.div`
  text-align: center;
  b {
    display: block;
    font-family: ${({ theme }) => theme.font.display};
    font-weight: 600;
    font-size: clamp(32px, 5vw, 48px);
    line-height: 1;
    color: ${({ theme }) => theme.colors.gold};
  }
  span {
    display: block;
    margin-top: 8px;
    font-size: 12.5px;
    letter-spacing: 0.03em;
    color: rgba(251, 247, 239, 0.72);
  }
`;

const Closing = styled.p`
  margin: clamp(40px, 6vw, 60px) auto 0;
  max-width: 560px;
  font-family: ${({ theme }) => theme.font.display};
  font-style: italic;
  font-size: clamp(18px, 2.6vw, 23px);
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.green};
`;

const Ctas = styled.div`
  margin-top: 28px;
  display: flex;
  gap: 14px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  .primary {
    background: ${({ theme }) => theme.colors.green};
    color: ${({ theme }) => theme.colors.cream};
    padding: 14px 30px;
    border-radius: 100px;
    font-weight: 500;
    font-size: 15px;
    transition: 0.25s;
  }
  .primary:hover { background: ${({ theme }) => theme.colors.greenDeep}; transform: translateY(-2px); }
  a:not(.primary) { color: ${({ theme }) => theme.colors.green}; font-weight: 500; font-size: 15px; }
`;
